import React from 'react';
import MathJax from '../../utils/mathjax3-react';
import addImageErrorListener from '../../utils/errorCapture';

const mathJaxHost = process.env.NODE_ENV === 'production' ? "https://app.chalktalk.com" : "";


export const MathJaxProvider = ({ children }) => {
  const options = React.useMemo(() => ({
    options: {
      ignoreHtmlClass: "lrn_noMath",
      safeOptions: {
        allow: {
          //
          //  Values can be "all", "safe", or "none"
          //
          URLs: 'safe',   // safe are in safeProtocols below
          classes: 'none',   // safe start with mjx- (can be set by pattern below)
          cssIDs: 'none',   // safe start with mjx- (can be set by pattern below)
          styles: 'none'    // safe are in safeStyles below
        },
        //
        //  Which URL protocols are allowed
        //
        safeProtocols: {
          http: false,
          https: true,
          file: false,
          javascript: false,
          data: false
        },
        scriptsizemultiplierRange: [.6, 1],     // Valid range for scriptsizemultiplier
        scriptlevelRange: [-2, 2],              // Valid range for scriptlevel
        dataPattern: /^data-mjx-/               // Pattern for data attributes
      }
    },
    loader: { load: ['ui/safe'] },
    tex: {
      inlineMath: [['\\(', '\\)']],
      macros: {
        abs: ['{|#1|}', 1],
        degree: ['°'],
        longdiv: ['{\\enclose{longdiv}{#1}}', 1],
        atomic: ['{_{#1}^{#2}}', 2],
        polyatomic: ['{_{#2}{}^{#1}}', 2],
        circledot: ['{\\odot}'],
        parallelogram: ['\\unicode{x25B1}'],
        ngtr: ['\\unicode{x226F}'],
        nless: ['\\unicode{x226E}'],
        MathQuillVarField: ['#1', 1],
        overarc: ['{\\overparen{#1}}', 1]
      }
    },
    chtml: {
      minScale: 1,
      matchFontHeight: false,
      fontURL: `${mathJaxHost}/shared/mathJax/v4/fonts/newcm`,
    },
  }), []);

  return (
    <MathJax.Provider
      url={`${mathJaxHost}/shared/mathJax/v4/tex-chtml.js`}
      options={options}
    >
      {children}
    </MathJax.Provider>
  );
};

const MathJaxItem = ({ data }) => {
  const [show, setShow] = React.useState(false);
  const assetRef = React.useRef(null);

  const addBoldTextClass = () => {
    if (!assetRef || !assetRef?.current) {
      return;
    }

    const bTags = assetRef.current.getElementsByTagName('b');
    for (let b = 0; b < bTags.length; b += 1) {
      if (bTags[b].textContent.includes('Strategy/Rule')) {
        bTags[b].classList.add('boldText');
      }
    }
  };

  React.useEffect(() => {
    if (show && assetRef?.current) {
      // Add title to iframes that lack one (fixes frame-title a11y violation)
      assetRef.current.querySelectorAll('iframe:not([title])').forEach((iframe) => {
        iframe.setAttribute('title', 'Embedded content');
      });
      // Hide empty headings (fixes empty-heading a11y violation)
      assetRef.current.querySelectorAll('h1,h2,h3,h4,h5,h6').forEach((heading) => {
        if (!heading.textContent.trim()) {
          heading.style.display = 'none';
        }
      });
      // Attach error listeners immediately, even before MathJax processes
      // This catches images that fail during MathJax processing or if MathJax fails to load
      const cleanup = addImageErrorListener(assetRef.current);
      return cleanup; // Cleanup observer on unmount
    }
  }, [data, show]);

  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      setShow(true);
      addBoldTextClass();
    }, 0);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [data]);

  return (
    show && (
      <div ref={assetRef}>
        <MathJax.Html html={data} />
      </div>
    )
  );
};

export default React.memo(MathJaxItem);
